import { i18n } from '@lingui/core'
import { t, Trans } from '@lingui/macro'
import { Alert, AlertTitle, Button, Dialog, DialogContent, DialogContentText, Grid, Typography } from '@mui/material'
import { dateFormat } from 'app/appSettings'
import { queryCreateObject } from 'app/backend/Common'
import { CaseToCreateSF, FormErrorCode } from 'app/backend/salesforce-maps/Case'
import { updateErrorLogByFlow } from 'app/backend/selesforce-custom-queries/ErrorLogQueries'
import { ReduxOrganization, ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { MAIN_PATH } from 'app/RootConstants'
import { matchPath, RouteConfig } from 'app/router/Routes'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import { createCaseByFlow } from 'app/services/sfAuth/sfData/sfCase'
import { HELP_CASE_TYPES } from 'app/services/sfAuth/sfData/sfCaseTypes'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { DialogTitleWithIconClose } from '../common-components/DialogTitleWithIconClose'
import { FormTopLabel } from '../common-components/forms/FormTopLabel'
import { TextFieldWithCustomControl } from '../common-components/with-formik/TextFieldWithCustomControl'
import fundingRoutes from '../funding/FundingRoutes'
import grantsRoutes from '../grants/GrantsRoutes'
import dmahRoutes from '../internal/DMAH/dmahRoutes'
import internalRoutes from '../internal/InternalRoutes'
import { resourceInventoryRoutes } from '../resources/ResourceRoutes'
import satRoutes from '../self-assessment/SATRoutes'
import sessionRoutes from '../sessions/SessionRoutes'
import surveyRoutes from '../surveys/SurveyRoutes'
import utilitiesRoutes from '../utilities/UtilitiesRoutes'
import vivreEnVilleRoutes from '../vivre-en-ville/VivreEnVilleRoutes'
import formsRoutes from './formsRoutes'
import { requiredTrans } from './formTranslations'
import { ConnectedMap } from './FormTypes'
import { getObjectsIdsFromConnectedMap } from './formUtils'

const FORM_LOADING_ERRORS: Record<string, { code: FormErrorCode, label: string }> = {
  BUDGET_UPDATE_NO_LINES: {
    code: 'LD_01',
    label: i18n._(t`FORM_BUDGET_UPDATE_NO_GRANTEE_BUDGET_LINES_ERROR_LABEL`)
  },
  MILESTONES_UPDATE_NO_BENCHMARKS: {
    code: 'LD_02',
    label: i18n._(t`FORM_BUDGET_UPDATE_NO_GRANTEE_BUDGET_LINES_ERROR_LABEL`)
  },
  OBJECTIVES_UPDATE_NO_OBJECTIVES: {
    code: 'LD_03',
    label: i18n._(t`FORM_BUDGET_UPDATE_NO_GRANTEE_BUDGET_LINES_ERROR_LABEL`)
  },
  SURVEY_IS_LOCKED: {
    code: 'S_01',
    label: i18n._(t`SURVEY_LOCKED_BY_ANOTHER_USER`)
  },
  NO_ID_PROVIDED: {
    code: 'G_01',
    label: i18n._(t`ID_NOT_PROVIDED_IN_URL`)
  },
  INSUFFICIENT_ACCESS: {
    code: 'G_02',
    label: i18n._(t`INSUFFICIENT_ACCESS`)
  }
}

export const ERROR_REPORTING_USER_ID = '005ON0000001s81YAA'

export const FormErrorPopUp: React.FC<{
  open: boolean
  connectedMap: ConnectedMap
  parent: 'Form' | 'Survey'
  mainConnectedObject: string
  type: 'Initial values' | 'Unexpected error'
  error: {
    message: string
    type: string
  }
}> = ({ open, error, type, connectedMap, mainConnectedObject, parent = 'Form' }) => {
  const [reportErrorOpen, setReportErrorOpen] = useState<boolean>(false)
  const [thankYouOpen, setThankYouOpen] = useState<boolean>(false)
  const [errorDescription, setErrorDescription] = useState<string>('')
  const [submittingCase, setSubmittingCase] = useState<boolean>(false)
  const [errorLogCreated, setErrorLogCreated] = useState<string | undefined>()
  const history = useHistory()
  const user = useSelector<ReduxState, ReduxUser>(state => state.user)
  const organization = useSelector<ReduxState, ReduxOrganization>((state) => state.organization)

  const routes: RouteConfig[] = [
    ...resourceInventoryRoutes,
    ...fundingRoutes,
    ...internalRoutes,
    ...grantsRoutes,
    ...sessionRoutes,
    ...utilitiesRoutes,
    ...formsRoutes,
    ...vivreEnVilleRoutes,
    ...dmahRoutes,
    ...satRoutes,
    ...surveyRoutes
  ]

  useEffect(() => {
    if (error != null) {
      queryCreateObject({
        type: 'Error Log',
        createData: {
          severity: 'error',
          description: sfOauthConfig.oauth2.redirectUri + String(history.location.pathname),
          longDescription: JSON.stringify(error)
        }
      }).then(result => {
        setErrorLogCreated(result.id)
      }, reject => {
        console.log(reject)
      })
    }
  }, [error])

  useEffect(() => {
    if (!open) {
      setErrorLogCreated(undefined)
    }
  }, [open])

  if (error == null) {
    return null
  }

  const errorData = FORM_LOADING_ERRORS[error.message] ?? {
    code: 'Unknown',
    label: error.message
  }

  const handleDone = (): void => {
    const pathname = history.location.pathname
    const routeObj: RouteConfig | undefined = routes.find(obj => matchPath(obj.path, pathname))
    if (routeObj?.errorRedirectPath != null) {
      history.replace(routeObj.errorRedirectPath)
    } else {
      history.push(MAIN_PATH)
    }
  }

  const submitErrorReport = async (): Promise<void> => {
    setSubmittingCase(true)
    const { connectedAccount, connectedOpportunity, connectedResource, connectedSurvey } = getObjectsIdsFromConnectedMap(connectedMap, mainConnectedObject)
    let errorStringified = `${errorData.label}`
    if (errorData.code != null) {
      errorStringified += ` (${errorData.code})`
    }

    const caseToCreate: CaseToCreateSF = {
      title: `Error accessing ${parent === 'Form' ? 'form' : 'survey'} ${moment.utc().format(dateFormat)}`,
      type: HELP_CASE_TYPES.HELP,
      language: user.language,
      contact: user.userObject.contactId,
      opportunityId: connectedOpportunity,
      description: errorDescription,
      location: sfOauthConfig.oauth2.redirectUri + String(history.location.pathname),
      resource: connectedResource,
      detailedDescription: errorStringified,
      organization: connectedAccount ?? organization.id,
      owner: ERROR_REPORTING_USER_ID,
      surveyId: connectedSurvey,
      errorCode: errorData.code
    }

    createCaseByFlow(caseToCreate).then(result => {
      setSubmittingCase(false)
      const resultValues = result[0].outputValues
      if (errorLogCreated != null) {
        updateErrorLogByFlow({
          id: errorLogCreated,
          caseId: resultValues.createdId
        }).finally(() => {})
      }
      setThankYouOpen(true)
    }).catch(error => {
      console.error(error)
      setSubmittingCase(false)
    })
  }

  return (
    <>
      <Dialog maxWidth='xs' fullWidth open={thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={() => {
            setThankYouOpen(false)
            handleDone()
          }}
          label={<Trans>THANK_YOU</Trans>}
        />
        <DialogContent>
          <DialogContentText sx={{ lineHeight: '20px' }}>
            <Trans>CASE_SUBMITTED_THANK_YOU_TEXT</Trans>
          </DialogContentText>
          <Button
            variant='outlined'
            fullWidth
            sx={{ mt: '8px' }}
            onClick={e => {
              setThankYouOpen(false)
              handleDone()
            }}
          >
            <Trans>CLOSE</Trans>
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog maxWidth='xs' fullWidth open={reportErrorOpen && !thankYouOpen}>
        <DialogTitleWithIconClose
          disabled={submittingCase}
          handleClose={() => {
            setReportErrorOpen(false)
          }}
          label={<Trans>REPORT_ERROR</Trans>}
        />
        <DialogContent>
          <Typography className='required-fields-label' sx={{ pt: '0px' }}>
            <Trans>
              REQUIRED_FIELDS_PROMPRT_EDITABLE_FORM
            </Trans>
          </Typography>

          <FormTopLabel required>
            <Trans>
              REPORT_ERROR_DESCRIPTION_LABEL
            </Trans>
          </FormTopLabel>

          <TextFieldWithCustomControl
            value={errorDescription}
            handleChange={v => {
              setErrorDescription(v)
            }}
            multiline
            rows={7}
            fullWidth
            required
            error={errorDescription === ''}
            errorLabel={requiredTrans}
            disabled={submittingCase}
          />
          <Grid sx={{ mt: '32px' }} container direction='column' gap='16px'>
            <Button
              color='primary'
              variant='contained'
              fullWidth
              disabled={submittingCase || errorDescription === ''}
              onClick={() => {
                submitErrorReport().catch(err => {
                  console.error(err)
                })
              }}
            >
              <Trans>SUBMIT</Trans>
            </Button>

            <Button
              color='primary'
              variant='outlined'
              fullWidth
              disabled={submittingCase}
              onClick={() => {
                setReportErrorOpen(false)
              }}
            >
              <Trans>CANCEL</Trans>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog maxWidth='xs' fullWidth open={open && !reportErrorOpen && !thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={handleDone}
          label={<Trans>ERROR</Trans>}
        />
        <DialogContent>
          <Alert
            severity='error'
          >
            <AlertTitle>
              <Trans>
                FORM_COULD_NOT_BE_ACCESSED
              </Trans>
              {errorData.code !== 'Unknown' ? ` (${i18n._(t`ERROR_CODE`)} ${errorData.code})` : ''}
            </AlertTitle>
            <Typography>
              <Trans>
                FORM_ACCESS_FAILED_PROMPT
              </Trans>
            </Typography>
          </Alert>

          <Grid sx={{ mt: '32px' }} container direction='column' gap='16px'>
            <Button
              color='primary'
              variant='contained'
              fullWidth
              onClick={() => {
                setReportErrorOpen(true)
              }}
            >
              <Trans>REPORT_ERROR</Trans>
            </Button>

            <Button
              color='primary'
              variant='outlined'
              fullWidth
              onClick={handleDone}
            >
              <Trans>CANCEL</Trans>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}
